import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { useRevokeSharedBlueprint } from 'features/blueprints';
import { Icons } from 'greener-ui-library';
import { useSnackbar } from 'notistack';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT } from 'utils/constants';
import ButtonWithConfirmationDialog from 'components/buttons/ButtonWithConfirmationDialog';

interface PdfSectionProps {
  pdfFileName: string;
  onDownloadPdf: () => void;
  isDownloading?: boolean;
}

export const PdfSection = ({ pdfFileName, onDownloadPdf, isDownloading = false }: PdfSectionProps) => {
  const { t } = useTranslation(['blueprints']);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2">{t('blueprints:share.pdfDocument')}</Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Typography variant="body1" color="primary">{pdfFileName}</Typography>

        <Button
          variant="outlined"
          color="secondary"
          onClick={onDownloadPdf}
          disabled={isDownloading}
          endIcon={
            isDownloading ? (
              <CircularProgress size={16} />
            ) : (
              <Icons.FileDownload sx={{ width: '16px', height: '16px' }} />
            )
          }
        >
          {t('blueprints:share.downloadPdf')}
        </Button>
      </Box>
    </Box>
  );
};

interface CopyableTextFieldProps {
  value: string;
  label: string;
  onCopy: () => void;
  isCopied: boolean;
  maxWidth?: Record<string, string>;
  flex?: Record<string, string>;
}

export const CopyableTextField = ({
  value,
  label,
  onCopy,
  isCopied,
  maxWidth = { xs: '100%', sm: '448px' },
  flex = { xs: '1 1 100%', lg: '1 1 auto' },
}: CopyableTextFieldProps) => {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex }}>
      <TextField
        data-testid="copyable-text-field"
        value={value}
        label={label}
        fullWidth
        size="small"
        InputProps={{
          readOnly: true,
        }}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{
          maxWidth,
        }}
      />
      <IconButton
        size="medium"
        onClick={onCopy}
        color={isCopied ? 'primary' : 'default'}
      >
        {isCopied ? <Icons.Check /> : <Icons.ContentCopy />}
      </IconButton>
    </Box>
  );
};

interface ExpirationDatePickerProps {
  selectedDate: Date;
  onChange: (date: Date | null) => void;
  label: string;
  disabled?: boolean;
}

export const ExpirationDatePicker = ({
  selectedDate,
  onChange,
  label,
  disabled = false,
}: ExpirationDatePickerProps) => {

  return (
    <DatePicker
      selected={selectedDate}
      onChange={onChange}
      dateFormat={DATE_FORMAT}
      showTimeSelect={false}
      minDate={new Date()}
      disabled={disabled}
      popperPlacement="bottom"
      customInput={
        <TextField
          data-testid="expiration-date-picker"
          label={label}
          variant="outlined"
          disabled={disabled}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Icons.ArrowDropDown />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            maxWidth: { xs: 'calc(100% - 48px)', sm: 'calc(100% - 104px)' },
            width: '100%',
          }}
        />
      }
    />
  );
};

interface DeactivateLinkButtonProps {
  onDeactivate: () => void;
  shareToken: string;
  blueprintId: number;
}

export const DeactivateLinkButton = ({ onDeactivate, shareToken, blueprintId }: DeactivateLinkButtonProps) => {
  const { t } = useTranslation(['blueprints']);
  const { enqueueSnackbar } = useSnackbar();
  const { revokeSharedBlueprint, isLoading: isRevoking } = useRevokeSharedBlueprint();

  const handleConfirm = async () => {
    try {
      await revokeSharedBlueprint({ shareToken, blueprintId });
      onDeactivate();
      enqueueSnackbar(t('blueprints:share.deactivateSuccess'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('blueprints:share.errors.deactivateFailed'), { variant: 'error' });
    }
  };

  return (
    <ButtonWithConfirmationDialog
      buttonProps={{
        color: 'error',
        disabled: isRevoking,
        sx: { pl: { xs: 0 } },
      }}
      buttonText={isRevoking ? <CircularProgress size={20} color="inherit" /> : t('blueprints:share.deactivateLink')}
      handleConfirm={handleConfirm}
      title={t('blueprints:share.deactivateConfirmation.title')}
      confirmButtonText={t('blueprints:share.deactivateConfirmation.confirm')}
      cancelButtonText={t('blueprints:share.deactivateConfirmation.cancel')}
      dialogConfirmButtonProps={{ disabled: isRevoking }}
    >
      <Typography variant="body1">
        {t('blueprints:share.deactivateConfirmation.message')}
      </Typography>
    </ButtonWithConfirmationDialog>
  );
};
